import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  CompositeDecorator,
} from "draft-js";
import { convertToHTML, convertFromHTML } from "draft-convert";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "draft-js/dist/Draft.css";
import PostToolbar from "../TribeTextEditor/PostToolbar";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  MENTION_ENTITY,
  mentionEntityStrategy,
  MentionSpan,
} from "../draftMention";
import { preserveExtraSpaces } from "../../Utils/preserveEditorWhitespace";

const mentionDecorator = new CompositeDecorator([
  { strategy: mentionEntityStrategy, component: MentionSpan },
]);

const getNodeAttr = (node, name) =>
  node.getAttribute?.(name) ?? node.attribs?.[name];
const getNodeClass = (node) => getNodeAttr(node, "class") || "";
const getNodeText = (node) =>
  node.textContent ?? (node.children || []).map((c) => c.data || "").join("");

const mentionConvertFromHTML = (html) =>
  convertFromHTML({
    htmlToEntity: (nodeName, node, createEntity) => {
      if (
        nodeName === "span" &&
        getNodeClass(node).includes("comment-mention")
      ) {
        const userId = getNodeAttr(node, "data-user-id") || "";
        const text = getNodeText(node);
        const fullName = text.startsWith("@") ? text.slice(1).trim() : text;
        return createEntity(MENTION_ENTITY, "IMMUTABLE", {
          userId: String(userId),
          fullName: fullName || "",
        });
      }
      return null;
    },
  })(html);

const CommentInput = ({
  postId,
  groupId,
  userProfile,
  onCommentPosted,
  replying = false,
  isEditing = false,
  commentText = null,
  replyToUser = null,
  members = [],
}) => {
  const [editorState, setEditorState] = useState(() => {
    if (isEditing && commentText) {
      try {
        const contentState = mentionConvertFromHTML(commentText);
        return EditorState.createWithContent(contentState, mentionDecorator);
      } catch (error) {
        console.error("Error converting HTML to editor state:", error);
        return EditorState.createEmpty(mentionDecorator);
      }
    }
    return EditorState.createEmpty(mentionDecorator);
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const emojiPickerRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const editorRef = useRef(null);
  const editorWrapperRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    left: 0,
    width: 0,
    placement: "below",
    top: 0,
    bottom: 0,
    maxHeight: 220,
  });
  const [effectiveMembers, setEffectiveMembers] = useState(members || []);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const hasText = editorState.getCurrentContent().hasText();
  const showToolbar =
    isFocused || hasText || isEditing || showTagModal || showEmojiPicker;
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (showTagModal) return;
    setEffectiveMembers(members || []);
  }, [members, showTagModal]);

  useEffect(() => {
    if (isEditing && commentText) {
      try {
        const contentState = mentionConvertFromHTML(commentText);
        setEditorState(
          EditorState.createWithContent(contentState, mentionDecorator),
        );
      } catch (error) {
        console.error("Error converting HTML to editor state:", error);
      }
    }
  }, [commentText, isEditing]);

  useEffect(() => {
    if (!showTagModal || !groupId) return;

    let cancelled = false;

    (async () => {
      try {
        setIsLoadingMembers(true);
        const aboutRes = await api.get(`user/group/about/${groupId}`);
        const fetchedMembers = aboutRes?.data?.data?.about?.members || [];
        if (!cancelled) setEffectiveMembers(fetchedMembers);
      } catch (e) {
        console.error("Failed to fetch group members", e);
      } finally {
        if (!cancelled) setIsLoadingMembers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showTagModal, groupId]);

  const recomputeDropdownPosition = () => {
    if (!editorWrapperRef.current) return;
    const rect = editorWrapperRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight;

    const horizontalPadding = 8;
    const desiredWidth = Math.max(rect.width - 32, 200);
    const maxWidth = Math.max(200, viewportW - horizontalPadding * 2);
    const width = Math.min(desiredWidth, maxWidth);

    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const desiredMaxHeight = 260;

    let placement = "below";
    if (spaceBelow >= 160) {
      placement = "below";
    } else if (spaceAbove >= spaceBelow) {
      placement = "above";
    } else {
      placement = "below";
    }

    let top = 0;
    let bottom = 0;
    let maxHeight = desiredMaxHeight;

    if (placement === "below") {
      top = rect.bottom + 8;
      maxHeight = Math.max(
        140,
        Math.min(desiredMaxHeight, viewportH - top - 8),
      );
    } else {
      bottom = viewportH - rect.top + 8;
      maxHeight = Math.max(140, Math.min(desiredMaxHeight, spaceAbove - 16));
    }

    let left = rect.left;
    if (left + width + horizontalPadding > viewportW) {
      left = Math.max(horizontalPadding, viewportW - width - horizontalPadding);
    }
    left = Math.max(horizontalPadding, left);

    setDropdownPosition({
      left,
      width,
      placement,
      top,
      bottom,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!showTagModal) return;
    recomputeDropdownPosition();
    const onWin = () => recomputeDropdownPosition();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [showTagModal]);

  // Auto grow editor until max height, then scroll
  useLayoutEffect(() => {
    if (!editorWrapperRef.current || showTagModal) return;

    const wrapper = editorWrapperRef.current;
    const draftContent = wrapper.querySelector(".public-DraftEditor-content");

    if (!draftContent) return;

    const minHeight = isFullscreen ? 300 : showToolbar ? 80 : 44;
    const maxHeight = isFullscreen
      ? Math.floor(window.innerHeight * 0.6)
      : showToolbar
        ? 300
        : 44;

    wrapper.style.height = "auto";

    const contentHeight =
      draftContent.scrollHeight + (showToolbar || isFullscreen ? 32 : 0);
    const finalHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));

    wrapper.style.height = `${finalHeight}px`;
    wrapper.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
  }, [editorState, isFullscreen, showToolbar, showTagModal]);

  useEffect(() => {
    if (!document.querySelector('script[src*="emoji-picker-element"]')) {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/emoji-picker-element@^1/index.js";
      script.type = "module";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!showEmojiPicker || !emojiPickerRef.current) return;

    const emojiPickerElement = emojiPickerRef.current;

    const timer = setTimeout(() => {
      if (!emojiPickerElement.shadowRoot) return;

      if (
        !emojiPickerElement.shadowRoot.querySelector(
          "[data-emoji-picker-theme]",
        )
      ) {
        const style = document.createElement("style");
        style.setAttribute("data-emoji-picker-theme", "");
        style.textContent = `
          .picker { border-radius: 20px; background-color: #121e29; } 
          .pad-top { background-color: #121e29; } 
          input.search {
            background-color: #121e29;
            color: #ffffff;
          }
          input.search::placeholder {
            color: rgba(255, 255, 255, 0.55);
          }
          .skintone-button-wrapper { background-color: #121e29; }
        `;
        emojiPickerElement.shadowRoot.appendChild(style);
      }

      emojiPickerElement.addEventListener("emoji-click", handleEmojiSelect);
      emojiPickerElement.shadowRoot.querySelector("input.search")?.focus();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (!emojiPickerElement) return;
      emojiPickerElement.removeEventListener("emoji-click", handleEmojiSelect);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickOutsideEmoji = (event) => {
      const pickerEl = emojiPickerRef.current;
      if (
        showEmojiPicker &&
        pickerEl &&
        !pickerEl.contains(event.target) &&
        !(event.composedPath && event.composedPath().includes(pickerEl)) &&
        event.target.id !== "emojiButton"
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideEmoji);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideEmoji);
    };
  }, [showEmojiPicker]);

  const handleEmojiSelect = (event) => {
    event.stopPropagation();
    const emoji = event.detail.unicode;

    setEditorState((prev) => {
      const contentState = prev.getCurrentContent();
      const selectionState = prev.getSelection();

      const newContentState = Modifier.insertText(
        contentState,
        selectionState,
        emoji,
      );

      return EditorState.push(prev, newContentState, "insert-characters");
    });
  };

  const handleInlineStyle = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const handleBlockType = (blockType) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const handleExpandEditor = () => {
    setIsExpanded((prev) => !prev);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  const handleTagUser = () => {
    setShowTagModal((prev) => !prev);
  };

  const [mentionSearchQuery, setMentionSearchQuery] = useState("");
  const didAutoTagRef = useRef(false);

  const getAlreadyMentionedUserIds = (contentState) => {
    const ids = new Set();
    contentState.getBlocksAsArray().forEach((block) => {
      block.findEntityRanges(
        (char) => {
          const key = char.getEntity();
          return (
            key !== null &&
            contentState.getEntity(key).getType() === MENTION_ENTITY
          );
        },
        (start) => {
          const key = block.getEntityAt(start);
          const data = contentState.getEntity(key).getData();
          if (data?.userId) ids.add(String(data.userId));
        },
      );
    });
    return ids;
  };

  const insertMention = (member) => {
    const content = editorState.getCurrentContent();
    const alreadyMentioned = getAlreadyMentionedUserIds(content);
    const targetId = String(member.user.id);
    if (alreadyMentioned.has(targetId)) {
      setShowTagModal(false);
      setMentionSearchQuery("");
      return;
    }

    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const text = block.getText();
    const offset = selection.getStartOffset();
    const textUpToCursor = text.slice(0, offset);
    const lastAtIndex = textUpToCursor.lastIndexOf("@");
    const startOffset = lastAtIndex >= 0 ? lastAtIndex : offset;

    const mentionLabel = `@${member.user.full_name}`;
    const contentWithEntity = content.createEntity(
      MENTION_ENTITY,
      "IMMUTABLE",
      { userId: targetId, fullName: member.user.full_name || "" },
    );
    const entityKey = contentWithEntity.getLastCreatedEntityKey();

    const mentionSelection = selection.merge({
      anchorKey: blockKey,
      anchorOffset: startOffset,
      focusKey: blockKey,
      focusOffset: offset,
    });

    const contentWithMention = Modifier.replaceText(
      contentWithEntity,
      mentionSelection,
      mentionLabel,
      null,
      entityKey,
    );

    const afterMentionOffset = startOffset + mentionLabel.length;
    const selectionAfterMention = mentionSelection.merge({
      anchorOffset: afterMentionOffset,
      focusOffset: afterMentionOffset,
    });

    const contentWithSpace = Modifier.insertText(
      contentWithMention,
      selectionAfterMention,
      " ",
      null,
      null,
    );

    setEditorState(
      EditorState.push(editorState, contentWithSpace, "insert-characters"),
    );
    setShowTagModal(false);
    setMentionSearchQuery("");
  };

  const insertMentionForUser = useCallback(
    (user) => {
      const content = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const blockKey = selection.getStartKey();
      const targetId = String(user.id);
      const mentionLabel = `@${user.full_name}`;
      const contentWithEntity = content.createEntity(
        MENTION_ENTITY,
        "IMMUTABLE",
        {
          userId: targetId,
          fullName: user.full_name || "",
        },
      );
      const entityKey = contentWithEntity.getLastCreatedEntityKey();

      const startSelection = selection.merge({
        anchorKey: blockKey,
        anchorOffset: 0,
        focusKey: blockKey,
        focusOffset: 0,
      });

      const contentWithMention = Modifier.replaceText(
        contentWithEntity,
        startSelection,
        mentionLabel,
        null,
        entityKey,
      );

      const selectionAfterMention = startSelection.merge({
        anchorOffset: mentionLabel.length,
        focusOffset: mentionLabel.length,
      });

      const contentWithSpace = Modifier.insertText(
        contentWithMention,
        selectionAfterMention,
        " ",
        null,
        null,
      );

      setEditorState(
        EditorState.push(editorState, contentWithSpace, "insert-characters"),
      );
    },
    [editorState],
  );

  useEffect(() => {
    if (!replying) return;
    if (isEditing) return;
    if (!replyToUser) return;
    if (didAutoTagRef.current) return;

    const user = replyToUser;
    if (!user?.id || !user?.full_name) return;

    const content = editorState.getCurrentContent();
    if (content.hasText()) return;

    const alreadyMentioned = getAlreadyMentionedUserIds(content);
    const targetId = String(user.id);
    if (alreadyMentioned.has(targetId)) return;

    didAutoTagRef.current = true;
    insertMentionForUser(user);
  }, [replying, isEditing, replyToUser, editorState, insertMentionForUser]);

  const handleTagUserClicked = (memberOrId) => {
    const member =
      typeof memberOrId === "object" && memberOrId?.user
        ? memberOrId
        : effectiveMembers?.find((m) => m.user?.id === memberOrId);
    if (member) insertMention(member);
  };

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    const selection = newEditorState.getSelection();
    const blockKey = selection.getStartKey();
    const content = newEditorState.getCurrentContent();
    const block = content.getBlockForKey(blockKey);
    const text = block.getText();
    const offset = selection.getStartOffset();
    const textUpToCursor = text.slice(0, offset);
    const lastAtIndex = textUpToCursor.lastIndexOf("@");

    if (lastAtIndex === -1) {
      setShowTagModal(false);
      setMentionSearchQuery("");
      return;
    }

    const charList = block.getCharacterList();
    const charAtAt = charList.get(lastAtIndex);
    const entityKeyAtAt = charAtAt?.getEntity();
    const atIsInsideMention =
      entityKeyAtAt &&
      content.getEntity(entityKeyAtAt).getType() === MENTION_ENTITY;

    if (atIsInsideMention) {
      setShowTagModal(false);
      setMentionSearchQuery("");
      return;
    }

    const textAfterAt = textUpToCursor.slice(lastAtIndex + 1);
    const isActiveQuery = /^[\w]*$/.test(textAfterAt);

    if (isActiveQuery) {
      setShowTagModal(true);
      setMentionSearchQuery(textAfterAt.toLowerCase());
    } else {
      setShowTagModal(false);
      setMentionSearchQuery("");
    }
  };

  const [loading, setLoading] = useState(false);

  const queryClient = useQueryClient();

  const handleCommentSubmit = async () => {
    if (loading) return;

    const contentState = editorState.getCurrentContent();

    const taggedUserIds = [];
    contentState.getBlocksAsArray().forEach((block) => {
      block.findEntityRanges(
        (char) => {
          const key = char.getEntity();
          return (
            key !== null &&
            contentState.getEntity(key).getType() === MENTION_ENTITY
          );
        },
        (start) => {
          const key = block.getEntityAt(start);
          const { userId } = contentState.getEntity(key).getData();
          if (userId && !taggedUserIds.includes(userId)) {
            taggedUserIds.push(userId);
          }
        },
      );
    });

    const text = convertToHTML({
      blockToHTML: (block) => {
        if (block.type === "unstyled" && block.text.length === 0) {
          return (
            <p>
              <br />
            </p>
          );
        }
        return null;
      },
      entityToHTML: (entity, originalText) => {
        if (entity.type === MENTION_ENTITY && entity.data?.userId) {
          const displayText = originalText || `@${entity.data.fullName || ""}`;
          return `<span class="comment-mention" data-user-id="${entity.data.userId}">${displayText}</span>`;
        }
        return originalText;
      },
    })(contentState);

    const preservedText = preserveExtraSpaces(text);

    if (!preservedText || preservedText === "<p><br></p>") return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append(
        isEditing ? "comment_id" : replying ? "comment_id" : "id",
        postId,
      );
      formData.append("text", preservedText);
      taggedUserIds.forEach((id) => formData.append("tagged_users[]", id));

      let url = "/user/post/comment";
      if (isEditing) {
        url = "/user/comment/edit";
      } else if (replying) {
        url = "/user/comment/reply";
      }

      await api.post(url, formData);

      setEditorState(EditorState.createEmpty(mentionDecorator));
      setIsFullscreen(false);
      setIsFocused(false);
      if (editorRef.current) editorRef.current.blur();

      if (onCommentPosted) {
        onCommentPosted();
      }
      queryClient.invalidateQueries({ queryKey: ["tribePostDetails", postId] });

      toast.success(
        isEditing
          ? "Comment updated successfully"
          : replying
            ? "Replied to comment successfully"
            : "Commented on post successfully",
      );
    } catch (error) {
      console.error("Comment failed", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleClose = () => {
    setIsFocused(false);
    setIsFullscreen(false);
    if (editorRef.current) editorRef.current.blur();
    if (!hasText) {
    }
  };

  return (
    <div
      className={`d-flex align-items-start ${isFullscreen ? "fullscreen-comment-editor" : ""}`}
      style={{
        gap: "12px",
        width: "100%",
        maxWidth: "100%",
        ...(isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(10, 23, 34, 0.95)",
              zIndex: 9999,
              padding: "40px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }
          : {}),
      }}
      onClick={() => isFullscreen && handleClose()}
    >
      {!isFullscreen && (
        <img
          src={userProfile?.image ?? "icons/james.svg"}
          alt="User"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            objectFit: "cover",
            marginTop: "4px",
            flexShrink: 0,
          }}
        />
      )}

      <div
        className="d-flex flex-column gap-2 flex-grow-1"
        style={{
          minWidth: 0,
          overflow: "hidden",
          maxWidth: isFullscreen ? "800px" : "100%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isEditing && (
          <div className="d-flex gap-1 mb-1">
            <h2
              className="text-body-b4 mb-0"
              style={{ color: "#008cff", fontWeight: "600" }}
            >
              Editing Comment
            </h2>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#0A1722",
            borderRadius: "20px",
            border: isFocused ? "1px solid #008CFF" : "1px solid #1B2C3A",
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: isFocused
              ? "0px 4px 16px rgba(0, 140, 255, 0.15)"
              : "0px 4px 12px rgba(0, 0, 0, 0.2)",
            transition: "all 0.2s ease",
            minHeight: isFullscreen ? "400px" : "auto",
          }}
        >
          {(showToolbar || isFullscreen) && (
            <div
              style={{
                backgroundColor: "rgba(18, 30, 41, 0.6)",
                padding: "8px 16px",
                borderBottom: "1px solid #1B2C3A",
                display: "flex",
                alignItems: "center",
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <PostToolbar
                editorState={editorState}
                onToggleInlineStyle={handleInlineStyle}
                onToggleBlockType={handleBlockType}
                onExpandEditor={handleExpandEditor}
                onEmojiSelect={toggleEmojiPicker}
                showEmojiPicker={showEmojiPicker}
                emojiPickerRef={emojiPickerRef}
                isExpanded={isExpanded}
                onUndo={() => setEditorState(EditorState.undo(editorState))}
                onRedo={() => setEditorState(EditorState.redo(editorState))}
                onFullscreen={toggleFullscreen}
                onClose={handleClose}
                variant="compact"
                onBookmarkIcon={handleTagUser}
                showTagModal={showTagModal}
                members={members}
                handleTagUserClicked={handleTagUserClicked}
                userProfile={userProfile}
                tagDropdownRef={tagDropdownRef}
                enableMediaActions={false}
                emojiPlacement="above"
                onFileSelect={() => {}}
                externalTagPopover
              />
            </div>
          )}

          <div
            ref={editorWrapperRef}
            className="commentEditor commentInputstyle"
            style={{
              width: "100%",
              boxSizing: "border-box",
              maxWidth: "100%",
              padding: showToolbar || isFullscreen ? "16px" : "0 16px",
              minHeight: isFullscreen ? "80px" : showToolbar ? "80px" : "44px",
              maxHeight: isFullscreen ? "60vh" : showToolbar ? "300px" : "44px",
              overflowY: showTagModal ? "visible" : "hidden",
              overflowX: "hidden",
              scrollbarWidth: "none",
              position: "relative",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            <Editor
              ref={editorRef}
              editorState={editorState}
              onChange={handleEditorChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                if (showEmojiPicker) return;
                setIsFocused(false);
                setTimeout(() => setShowTagModal(false), 200);
              }}
              placeholder="Add your comment..."
              readOnly={loading}
              handleReturn={(e) => {
                if (!e.shiftKey && !loading) {
                  handleCommentSubmit();
                  return "handled";
                }
                return "not-handled";
              }}
            />

            {showTagModal &&
              createPortal(
                <div
                  ref={tagDropdownRef}
                  className="d-flex flex-column p-2"
                  style={{
                    position: "fixed",
                    left: dropdownPosition.left,
                    ...(dropdownPosition.placement === "below"
                      ? { top: dropdownPosition.top }
                      : { bottom: dropdownPosition.bottom }),
                    width: dropdownPosition.width,
                    maxHeight: dropdownPosition.maxHeight,
                    overflowY: "auto",
                    zIndex: 99999,
                    backgroundColor: "rgb(28, 42, 55)",
                    border: "1px solid rgb(42, 64, 82)",
                    borderRadius: "10px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.6)",
                      padding: "6px 8px 8px",
                      borderBottom: "1px solid rgba(42, 64, 82, 0.6)",
                    }}
                  >
                    Mention a group member — type to search
                  </div>

                  {effectiveMembers?.length > 0 ? (
                    (() => {
                      const alreadyMentioned = getAlreadyMentionedUserIds(
                        editorState.getCurrentContent(),
                      );

                      const filtered = (effectiveMembers || [])
                        .filter((m) => m.user?.id !== userProfile?.id)
                        .filter(
                          (m) => !alreadyMentioned.has(String(m.user?.id)),
                        )
                        .filter((m) => {
                          const name = (m.user?.full_name || "").toLowerCase();
                          return (
                            !mentionSearchQuery ||
                            name.includes(mentionSearchQuery)
                          );
                        });

                      return filtered.length > 0 ? (
                        filtered.map((member) => (
                          <div
                            key={member.user.id}
                            role="button"
                            tabIndex={0}
                            className="d-flex align-items-center gap-2"
                            style={{
                              fontSize: "14px",
                              cursor: "pointer",
                              padding: "8px 10px",
                              borderRadius: "8px",
                              color: "#E6ECF1",
                            }}
                            onClick={() => handleTagUserClicked(member)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleTagUserClicked(member);
                              }
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 140, 255, 0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            <img
                              src={member.user.image || "/icons/james.svg"}
                              alt={member.user.full_name}
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{ fontWeight: "600", fontSize: "13px" }}
                            >
                              {member.user.full_name}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "rgba(255,255,255,0.6)",
                            padding: "12px 10px",
                          }}
                        >
                          No matching members
                        </div>
                      );
                    })()
                  ) : isLoadingMembers ? (
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.6)",
                        padding: "12px 10px",
                      }}
                    >
                      Loading members...
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.6)",
                        padding: "12px 10px",
                      }}
                    >
                      No members to tag
                    </div>
                  )}
                </div>,
                document.body,
              )}
          </div>

          {(showToolbar || isFullscreen) && (
            <div
              style={{
                padding: "0 16px 16px 16px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                onClick={handleCommentSubmit}
                disabled={loading || !hasText}
                style={{
                  backgroundColor: "#008CFF",
                  color: "white",
                  border: "none",
                  borderRadius: "24px",
                  padding: "8px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontWeight: "700",
                  fontSize: "13px",
                  fontFamily: "'Lexend', sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  cursor: loading || !hasText ? "not-allowed" : "pointer",
                  opacity: loading || !hasText ? 0.6 : 1,
                  transition: "all 0.2s ease",
                  boxShadow: "0px 4px 8px rgba(0, 140, 255, 0.3)",
                }}
              >
                <span style={{ marginTop: "1px" }}>SEND</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    stroke: "white",
                    strokeWidth: "2.5",
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                  }}
                >
                  <path d="M4 10H16" />
                  <path d="M10 4L16 10L10 16" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentInput;
